/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___   ____  ____   ___
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _| |  _ \|  _ \ / _ \
 | |_| | | | | |_) || |  / / | | |  \| | | | | || |  | |_) | |_) | | | |
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |  |  __/|  _ <| |_| |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___| |_|   |_| \_\\___/

=========================================================
* Horizon UI Dashboard PRO - v1.0.0
=========================================================

* Product Page: https://www.horizon-ui.com/pro/
* Copyright 2022 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// Chakra imports
import {
  Box,
  Card,
  Center,
  Spinner,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Flex,
  Icon,
  useToken,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { ConnectedPartnerTable } from './internal/ConnectedPartnerTable';
import axios from 'axios';
import { getBaseUrl } from 'utils/url';
import { useAuth } from 'contexts/AuthContext';
import {
  PartnerResults,
  type PartnerSearchResultRow,
} from './internal/PartnerResults';
import { NavLink } from 'react-router-dom';
import { stripeService } from 'services/stripeService';
import { StripeSubscription, BackendSubscription } from 'types/stripe';
import FirebaseAnalyticsService from 'services/firebaseAnalyticsService';
import { HiSparkles } from 'react-icons/hi2';

/** POST /api/partner-recommendations — long-running; client timeout in ms */
const PARTNER_RECOMMENDATIONS_TIMEOUT_MS = 180_000;

type PartnerRecommendationApiItem = {
  rank: number;
  displayName: string;
  categoryLine: string;
  fitScore: number;
  rationale: string;
  source: string;
  partnerId: string;
  website: string;
  evidence: string;
};

function mapPartnerRecommendationToRow(
  p: PartnerRecommendationApiItem,
): PartnerSearchResultRow {
  const slug = p.displayName.replace(/\s+/g, '-').slice(0, 48);
  const syntheticId = `oon-${p.rank}-${slug}`;
  const pid = p.partnerId?.trim();
  return {
    id: pid || syntheticId,
    companyName: p.displayName,
    hubspotEmail: '',
    hubspotPortalId: pid || String(p.rank),
    hubspotName: '',
    createdAt: '',
    industry: p.categoryLine?.split('·')[0]?.trim() ?? '',
    icp: p.categoryLine?.trim()
      ? { targetIndustries: [p.categoryLine.trim()] }
      : null,
    products: '',
    targetMarket: [p.rationale?.trim(), p.evidence?.trim()]
      .filter(Boolean)
      .join('\n\n'),
    website: p.website ?? '',
    logoUrl: null,
    receiverAccountIds: [],
    notifyTeamMembersOnInvite: false,
    connectionStatus: '',
    partnerId: pid,
    fitScore:
      typeof p.fitScore === 'number'
        ? Math.min(100, Math.max(0, p.fitScore))
        : undefined,
    source:
      p.source === 'in_network' || p.source === 'out_of_network'
        ? p.source
        : undefined,
  };
}

export const Partners = () => {
  const { token, isOrgAdmin } = useAuth() ?? {};
  const [brand300, brand400] = useToken('colors', [
    'brand.300',
    'brand.400',
  ]);

  const [relationships, setRelationships] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [filteredPartnersLoading, setFilteredPartnersLoading] = useState(false);
  const [recommendedPartners, setRecommendedPartners] = useState<
    PartnerSearchResultRow[]
  >([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [recommendationsSummary, setRecommendationsSummary] = useState('');
  const [recommendationsError, setRecommendationsError] = useState<
    string | null
  >(null);
  const [subscriptions, setSubscriptions] = useState<
    StripeSubscription | BackendSubscription | null
  >(null);

  const fetchRelationships = useCallback(() => {
    setLoading(true);
    axios
      .get(`${getBaseUrl()}/api/partners/relationships`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .then((res) => {
        setRelationships(res.data);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  }, [token]);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const fetchedSubscriptions =
        await stripeService.getCustomerSubscriptions(token);
      setSubscriptions(fetchedSubscriptions[0] || null);
      const primary = fetchedSubscriptions[0];
      if (primary) {
        FirebaseAnalyticsService.setUserContext({
          plan_tier: String(primary.tier || 'unknown'),
        });
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  }, [token]);

  // Helper function to get active partners count
  const getActivePartnersCount = () => {
    if (!subscriptions) return 0;
    return subscriptions.activePartners || 0;
  };

  const fetchPartnerRecommendations = useCallback(async () => {
    if (!token) return;
    setRecommendationsLoading(true);
    setRecommendationsError(null);
    FirebaseAnalyticsService.trackFeatureUsage(
      'Partner Recommendations',
      'load_started',
    );
    try {
      const res = await axios.post(
        `${getBaseUrl()}/api/partner-recommendations`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: PARTNER_RECOMMENDATIONS_TIMEOUT_MS,
        },
      );
      const payload = res.data?.data;

      const rows = Array.isArray(payload?.partners)
        ? payload.partners.map(mapPartnerRecommendationToRow)
        : [];
      setRecommendedPartners(rows);
      setRecommendationsSummary(payload?.summary?.userFacingSummary ?? '');
      FirebaseAnalyticsService.trackFeatureUsage(
        'Partner Recommendations',
        'load_completed',
        { result_count: rows.length },
      );
    } catch (error: unknown) {
      const message =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'message' in error.response.data
          ? String(
              (error.response.data as { message?: unknown }).message ??
                'Request failed',
            )
          : 'Could not load partner recommendations.';
      setRecommendationsError(message);
      setRecommendedPartners([]);
      setRecommendationsSummary('');
      FirebaseAnalyticsService.trackFeatureUsage(
        'Partner Recommendations',
        'load_failed',
      );
    } finally {
      setRecommendationsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Track page view
    FirebaseAnalyticsService.trackNavigation('Partners', '/admin/ecosystem');

    fetchRelationships();
    fetchSubscriptions();
  }, [fetchRelationships, fetchSubscriptions]);

  useEffect(() => {
    fetchPartnerRecommendations();
  }, [fetchPartnerRecommendations]);

  const searchPartners = useCallback(
    (query: string) => {
      if (!query || query.length < 3) {
        setFilteredPartners([]);
        setFilteredPartnersLoading(false);
        return;
      }

      // Track partner search
      FirebaseAnalyticsService.trackFeatureUsage(
        'Partner Search',
        'search_initiated',
        {
          query_length: query.length,
          search_term: query.substring(0, 20), // Only track first 20 chars for privacy
        },
      );

      setFilteredPartnersLoading(true);
      axios
        .get(`${getBaseUrl()}/api/partners/search?companyName=${query}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((res) => {
          setFilteredPartners(res.data.partners);

          // Track search results
          FirebaseAnalyticsService.trackFeatureUsage(
            'Partner Search',
            'search_completed',
            {
              results_count: res.data.partners.length,
              query_length: query.length,
            },
          );
        })
        .catch(() => {
          FirebaseAnalyticsService.trackFeatureUsage(
            'Partner Search',
            'search_failed',
            {
              query_length: query.length,
            },
          );
        })
        .finally(() => {
          setFilteredPartnersLoading(false);
        });
    },
    [token],
  );
  const handleInvitationResponse = useCallback(
    (invitationId: string, response: string) => {
      // Track invitation response
      FirebaseAnalyticsService.trackFeatureUsage(
        'Partner Invitation',
        'invitation_response',
        {
          response_type: response,
          invitation_id: invitationId,
        },
      );

      axios
        .post(
          `${getBaseUrl()}/api/partners/invites/respond`,
          {
            inviteId: invitationId,
            response,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .then(() => {
          fetchRelationships();

          // Track successful response
          FirebaseAnalyticsService.trackFeatureUsage(
            'Partner Invitation',
            'invitation_response_success',
            {
              response_type: response,
            },
          );
        })
        .catch(() => {
          FirebaseAnalyticsService.trackFeatureUsage(
            'Partner Invitation',
            'invitation_response_failed',
            {
              response_type: response,
            },
          );
        });
    },
    [token, fetchRelationships],
  );

  const handleInvitePartner = useCallback(
    (partnerId: string, receiverAccountIds?: string[]) => {
      // Track partner invitation
      FirebaseAnalyticsService.trackFeatureUsage(
        'Partner Management',
        'invite_partner',
        {
          partner_id: partnerId,
        },
      );

      const ids = receiverAccountIds?.filter(Boolean) ?? [];
      const body: {
        partnerId: string;
        receiverAccountIds?: string[];
        receiverAccountId?: string;
      } = { partnerId };

      if (ids.length > 0) {
        body.receiverAccountIds = ids;
        if (ids.length === 1) {
          body.receiverAccountId = ids[0];
        }
      }

      axios
        .post(`${getBaseUrl()}/api/partners/invite`, body, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(() => {
          fetchRelationships();

          // Track successful invitation
          FirebaseAnalyticsService.trackFeatureUsage(
            'Partner Management',
            'invite_partner_success',
            {
              partner_id: partnerId,
            },
          );
        })
        .catch(() => {
          FirebaseAnalyticsService.trackFeatureUsage(
            'Partner Management',
            'invite_partner_failed',
            {
              partner_id: partnerId,
            },
          );
        });
    },
    [token, fetchRelationships],
  );

  const handlePartnerDisconnect = useCallback(
    (partnerId: string) => {
      // Track partner disconnection
      FirebaseAnalyticsService.trackFeatureUsage(
        'Partner Management',
        'disconnect_partner',
        {
          partner_id: partnerId,
        },
      );

      axios
        .post(
          `${getBaseUrl()}/api/partners/disconnect`,
          {
            partnerId,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .then(() => {
          fetchRelationships();

          // Track successful disconnection
          FirebaseAnalyticsService.trackFeatureUsage(
            'Partner Management',
            'disconnect_partner_success',
            {
              partner_id: partnerId,
            },
          );
        })
        .catch(() => {
          FirebaseAnalyticsService.trackFeatureUsage(
            'Partner Management',
            'disconnect_partner_failed',
            {
              partner_id: partnerId,
            },
          );
        });
    },
    [token, fetchRelationships],
  );

  const handleCancelInvite = useCallback(
    (invitationId: string) => {
      FirebaseAnalyticsService.trackFeatureUsage(
        'Partner Management',
        'cancel_invite',
        { invite_id: invitationId },
      );
      axios
        .post(
          `${getBaseUrl()}/api/partners/invite/cancel`,
          { inviteId: invitationId },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .then(() => {
          FirebaseAnalyticsService.trackFeatureUsage(
            'Partner Management',
            'cancel_invite_success',
            { invite_id: invitationId },
          );
          fetchRelationships();
        })
        .catch(() => {
          FirebaseAnalyticsService.trackFeatureUsage(
            'Partner Management',
            'cancel_invite_failed',
            { invite_id: invitationId },
          );
        });
    },
    [token, fetchRelationships],
  );

  const handleSendEmailInvite = useCallback(
    ({ to, partnerName }: { to: string; partnerName?: string }) => {
      // Track email invitation
      FirebaseAnalyticsService.trackFeatureUsage(
        'Partner Management',
        'send_email_invite',
        {
          partner_name: partnerName || 'unknown',
          has_partner_name: !!partnerName,
        },
      );

      axios
        .post(
          `${getBaseUrl()}/api/email/partner-invitation`,
          {
            to,
            partnerName: partnerName || '',
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .then(() => {
          FirebaseAnalyticsService.trackFeatureUsage(
            'Partner Management',
            'send_email_invite_success',
            {
              partner_name: partnerName || 'unknown',
            },
          );
        })
        .catch(() => {
          FirebaseAnalyticsService.trackFeatureUsage(
            'Partner Management',
            'send_email_invite_failed',
            {
              partner_name: partnerName || 'unknown',
            },
          );
        });
    },
    [token],
  );

  if (loading) {
    return (
      <Center w="100%" h="50vh">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="brand.400"
          size="xl"
        />
      </Center>
    );
  }

  const partnerInvitations =
    (relationships &&
      relationships.invitations &&
      relationships.invitations.map((item: any) => ({
        companyName:
          (item.receiver && item.receiver.companyName) ||
          (item.sender && item.sender.companyName),
        email:
          (item.receiver && item.receiver.hubspotEmail) ||
          (item.sender && item.sender.hubspotEmail),
        portalId:
          (item.receiver && item.receiver.hubspotPortalId) ||
          (item.sender && item.sender.hubspotPortalId),
        dateConnected:
          (item.connectedAt && item.connectedAt._seconds * 1000) || null,
        logoUrl:
          (item.receiver && item.receiver.logoUrl) ||
          (item.sender && item.sender.logoUrl),
        status: item.status,
        invitationId: item.id,
        type: item.type,
      }))) ||
    [];

  const filteredPartnersResults = filteredPartners.map((partner: any) => {
    const portalId = partner.id ?? partner.hubspotPortalId;
    const receiverAccountIds: string[] = Array.isArray(
      partner.receiverAccountIds,
    )
      ? partner.receiverAccountIds.map(String)
      : [];
    return {
      id: String(partner.id ?? portalId),
      companyName: partner.companyName ?? '',
      hubspotEmail: partner.hubspotEmail ?? '',
      hubspotPortalId: partner.hubspotPortalId ?? portalId,
      hubspotName: partner.hubspotName ?? '',
      createdAt: partner.createdAt ?? '',
      industry: partner.industry ?? '',
      icp: partner.icp ?? null,
      products: partner.products ?? '',
      targetMarket: partner.targetMarket ?? '',
      website: partner.website ?? '',
      logoUrl: partner.logoUrl ?? null,
      receiverAccountIds,
      notifyTeamMembersOnInvite: Boolean(partner.notifyTeamMembersOnInvite),
      connectionStatus: partner.connectionStatus ?? '',
      partnerId: String(portalId),
      fitScore:
        typeof partner.fitScore === 'number'
          ? Math.min(100, Math.max(0, partner.fitScore))
          : undefined,
      source:
        partner.source === 'in_network' || partner.source === 'out_of_network'
          ? partner.source
          : undefined,
    };
  });

  return (
    <Box w="100%" minH="100vh" mt="100px" px={{ base: 2, md: 8 }} py={4}>
      {subscriptions && !subscriptions.hasConnectedPortal && (
        <Alert status="warning" mb={6} borderRadius="md">
          <AlertIcon />
          <AlertTitle color="#9A5C00">Portal not connected</AlertTitle>
          <AlertDescription color="#9A5C00">
            Some features will be unavailable until you connect your portal.{' '}
            <NavLink
              to="/admin/settings"
              style={{
                color: '#9A5C00',
                fontWeight: '600',
                textDecoration: 'underline',
              }}
            >
              Connect here
            </NavLink>
            .
          </AlertDescription>
        </Alert>
      )}
      {subscriptions?.tier === 'free' && (
        <Alert status="warning" mb={6} borderRadius="md">
          <AlertIcon />
          <AlertTitle color="#9A5C00">Free tier</AlertTitle>
          <AlertDescription color="#9A5C00">
            You have used {getActivePartnersCount()} /{' '}
            {subscriptions?.limits.partners} of your partner connections. To
            unlock unlimited connections,{' '}
            <NavLink
              to="/admin/settings"
              style={{
                color: '#9A5C00',
                fontWeight: '600',
                textDecoration: 'underline',
              }}
              onClick={() => {
                FirebaseAnalyticsService.trackFeatureUsage(
                  'Billing',
                  'paywall_cta_clicked',
                  { source: 'partners_free_tier_banner' },
                );
              }}
            >
              upgrade your plan here
            </NavLink>
            .
          </AlertDescription>
        </Alert>
      )}

      <Box display="flex" flexDirection="column" gap={6}>
        <Card w="100%" p="20px" borderRadius="12px">
          <Flex align="center" gap={3} mb={3}>
            <Flex
              align="center"
              justify="center"
              w="35px"
              h="35px"
              borderRadius="lg"
              backgroundImage={`linear-gradient(to bottom right, ${brand300}, ${brand400})`}
              color="white"
              flexShrink={0}
            >
              <Icon as={HiSparkles} boxSize="22px" aria-hidden />
            </Flex>
            <Text fontSize="2xl" fontWeight="bold">
              Recommended partners
            </Text>
          </Flex>
          <PartnerResults
            partnerResults={filteredPartnersResults}
            partnersLoading={filteredPartnersLoading}
            recommendedPartners={recommendedPartners}
            recommendationsLoading={recommendationsLoading}
            recommendationsSummary={recommendationsSummary}
            recommendationsError={recommendationsError}
            onSearchPartners={searchPartners}
            loading={filteredPartnersLoading}
            onInvitePartner={handleInvitePartner}
            onSendEmailInvite={handleSendEmailInvite}
            hasConnectedPortal={subscriptions?.hasConnectedPortal}
            activePartners={subscriptions?.activePartners || 0}
            partnerLimit={subscriptions?.limits?.partners || 0}
          />
        </Card>
        <Card w="100%" p="20px" borderRadius="12px">
          <Text fontSize="2xl" fontWeight="bold" mb={6}>
            Current Partners
          </Text>
          <ConnectedPartnerTable
            tableData={partnerInvitations}
            onInvitationResponse={handleInvitationResponse}
            onPartnerDisconnect={handlePartnerDisconnect}
            onCancelInvite={handleCancelInvite}
            onInvitePartner={handleInvitePartner}
            disableAdminPartnerActions={!isOrgAdmin}
          />
        </Card>
      </Box>
    </Box>
  );
};
